// Organizer Database Repository
// Saves all organizer data to Turso database instead of localStorage

import { db } from '@/database/Database';

export interface NotebookRecord {
  id: string;
  user_id: string;
  notebook_data: string; // JSON serialized ClassNotebook
  created_at: string;
  updated_at: string;
}

export interface MeetingTranscriptRecord {
  id: string;
  user_id: string;
  transcript_data: string; // JSON serialized MeetingTranscript
  created_at: string;
}

export interface TimelineOverrideRecord {
  id: string;
  user_id: string;
  override_data: string; // JSON serialized timeline overrides
  created_at: string;
  updated_at: string;
}

export interface ScheduledSessionRecord {
  id: string;
  user_id: string;
  session_data: string; // JSON serialized scheduled session
  created_at: string;
}

// NOTEBOOKS
export async function saveNotebooks(userId: string, notebooks: any[]) {
  console.log('[OrganizerDB] Saving notebooks for user:', userId, 'count:', notebooks?.length);
  
  // ============================================================================
  // CRITICAL SAFETY CHECKS - PREVENT DATA LOSS
  // ============================================================================
  
  // 1. Validate userId FIRST (before any database operations)
  if (!userId || userId.trim() === '') {
    console.error('[OrganizerDB] 🚨 BLOCKED: Invalid user ID');
    throw new Error('Invalid user ID - cannot save notebooks');
  }
  
  // 2. CRITICAL: Reject empty or invalid data - NEVER delete all data
  if (!notebooks || notebooks.length === 0) {
    console.error('[OrganizerDB] 🚨 BLOCKED: Attempt to save empty notebooks array!');
    console.error('[OrganizerDB] User ID:', userId);
    console.error('[OrganizerDB] This would DELETE all existing data without replacement.');
    console.error('[OrganizerDB] Keeping existing data intact.');
    console.trace('[OrganizerDB] Stack trace:');
    throw new Error('Cannot save empty notebooks - would cause data loss. Operation blocked.');
  }
  
  // 3. Validate notebook structure
  const hasInvalidNotebooks = notebooks.some(nb => !nb.id || !nb.title);
  if (hasInvalidNotebooks) {
    console.error('[OrganizerDB] 🚨 BLOCKED: Invalid notebook structure detected');
    console.error('[OrganizerDB] All notebooks must have id and title');
    throw new Error('Invalid notebook data - missing required fields');
  }
  
  // 4. SAFER APPROACH: Update existing row instead of delete-then-insert
  const existing = await db.query('SELECT * FROM notebooks WHERE user_id = ?', [userId]);
  
  if (existing.length > 0) {
    // UPDATE existing row - NEVER DELETE
    console.log('[OrganizerDB] 📝 Updating existing notebook row...');
    const existingId = existing[0].id;
    await db.query(
      'UPDATE notebooks SET content = ?, updated_at = ? WHERE id = ?',
      [JSON.stringify(notebooks), new Date().toISOString(), existingId]
    );
    console.log('[OrganizerDB] ✅ Successfully UPDATED', notebooks.length, 'notebooks');
  } else {
    // INSERT new row only if none exists
    console.log('[OrganizerDB] 📝 Creating new notebook row...');
    const id = `notebooks_${userId}_${Date.now()}`;
    await db.query(
      'INSERT INTO notebooks (id, user_id, organizer_type, name, content, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?)',
      [id, userId, 'student', 'All Notebooks', JSON.stringify(notebooks), new Date().toISOString(), new Date().toISOString()]
    );
    console.log('[OrganizerDB] ✅ Successfully CREATED with', notebooks.length, 'notebooks');
  }
}

export async function loadNotebooks(userId: string): Promise<any[]> {
  console.log('[OrganizerDB] Loading notebooks for user:', userId);
  
  const results = await db.query('SELECT * FROM notebooks WHERE user_id = ?', [userId]);
  
  if (results.length === 0) {
    console.log('[OrganizerDB] No notebooks found');
    return [];
  }

  // Parse the JSON content back to array with guard for corrupt rows
  try {
    const notebooks = JSON.parse(results[0].content);
    console.log('[OrganizerDB] Loaded', notebooks.length, 'notebooks');
    return notebooks;
  } catch (err) {
    console.error('[OrganizerDB] Failed to parse notebooks JSON; deleting corrupt row and returning empty list', err);
    try {
      await db.query('DELETE FROM notebooks WHERE user_id = ?', [userId]);
      console.log('[OrganizerDB] Deleted corrupt notebooks row for user', userId);
    } catch (deleteErr) {
      console.error('[OrganizerDB] Failed to delete corrupt notebooks row for user', userId, deleteErr);
    }
    return [];
  }
}

// MEETING TRANSCRIPTS
export async function saveMeetingTranscripts(userId: string, transcripts: any[]) {
  console.log('[OrganizerDB] Saving meeting transcripts for user:', userId);
  
  // Clear existing transcripts for this user
  await db.query('DELETE FROM meeting_transcripts WHERE user_id = ?', [userId]);
  
  // Insert all transcripts
  for (const transcript of transcripts) {
    await db.query(
      'INSERT INTO meeting_transcripts (id, user_id, title, content, meeting_date, created_at) VALUES (?, ?, ?, ?, ?, ?)',
      [
        transcript.id, 
        userId, 
        transcript.topic || 'Meeting', 
        JSON.stringify(transcript),
        transcript.date || new Date().toISOString(),
        transcript.created_at || new Date().toISOString()
      ]
    );
  }
  
  console.log('[OrganizerDB] Saved', transcripts.length, 'transcripts');
}

export async function loadMeetingTranscripts(userId: string): Promise<any[]> {
  console.log('[OrganizerDB] Loading meeting transcripts for user:', userId);
  
  const results = await db.query('SELECT * FROM meeting_transcripts WHERE user_id = ?', [userId]);
  const transcripts: any[] = [];
  for (const row of results) {
    try {
      transcripts.push(JSON.parse(row.content));
    } catch (err) {
      console.error('[OrganizerDB] Skipping corrupt transcript row', row?.id, err);
    }
  }

  console.log('[OrganizerDB] Loaded', transcripts.length, 'transcripts');
  return transcripts;
}

// TIMELINE OVERRIDES
export async function saveTimelineOverrides(userId: string, overrides: any) {
  console.log('[OrganizerDB] Saving timeline overrides for user:', userId);
  
  // Delete existing
  await db.query('DELETE FROM timeline_overrides WHERE user_id = ?', [userId]);
  
  // Insert new
  const id = `override_${userId}_${Date.now()}`;
  await db.query(
    'INSERT INTO timeline_overrides (id, user_id, override_data, created_at, updated_at) VALUES (?, ?, ?, ?, ?)',
    [id, userId, JSON.stringify(overrides), new Date().toISOString(), new Date().toISOString()]
  );
  
  console.log('[OrganizerDB] Saved timeline overrides');
}

export async function loadTimelineOverrides(userId: string): Promise<any> {
  console.log('[OrganizerDB] Loading timeline overrides for user:', userId);
  
  const results = await db.query('SELECT * FROM timeline_overrides WHERE user_id = ? ORDER BY updated_at DESC LIMIT 1', [userId]);
  
  if (results.length === 0) {
    console.log('[OrganizerDB] No timeline overrides found');
    return {};
  }
  
  const overrides = JSON.parse(results[0].override_data);
  console.log('[OrganizerDB] Loaded timeline overrides');
  return overrides;
}

// SCHEDULED SESSIONS
export async function saveScheduledSessions(userId: string, sessions: any[]) {
  console.log('[OrganizerDB] Saving scheduled sessions for user:', userId);
  
  // Clear existing sessions for this user
  await db.query('DELETE FROM scheduled_sessions WHERE user_id = ?', [userId]);
  
  // Insert all sessions
  for (const session of sessions) {
    // Generate unique ID if not present
    const sessionId = String(
      session.id || 
      session.sessionId || 
      `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    );
    
    // Map roomType to session_type, or use 'virtual_meeting' as default
    const sessionType = session.roomType || session.type || 'virtual_meeting';
    
    // Combine date + time or use session_date or fallback to createdAt
    let sessionDate;
    if (session.date && session.time) {
      sessionDate = `${session.date}T${session.time}:00`;
    } else {
      sessionDate = session.session_date || session.date || session.createdAt || new Date().toISOString();
    }
    
    const createdAt = session.createdAt || session.created_at || new Date().toISOString();
    
    await db.query(
      'INSERT INTO scheduled_sessions (id, user_id, session_type, session_date, session_data, created_at) VALUES (?, ?, ?, ?, ?, ?)',
      [sessionId, userId, sessionType, sessionDate, JSON.stringify(session), createdAt]
    );
  }
  
  console.log('[OrganizerDB] Saved', sessions.length, 'scheduled sessions');
}

export async function loadScheduledSessions(userId: string): Promise<any[]> {
  console.log('[OrganizerDB] Loading scheduled sessions for user:', userId);
  
  const results = await db.query('SELECT * FROM scheduled_sessions WHERE user_id = ?', [userId]);
  const sessions: any[] = [];
  for (const row of results) {
    try {
      sessions.push(JSON.parse(row.session_data));
    } catch (err) {
      console.error('[OrganizerDB] Skipping corrupt scheduled session row', row?.id, err);
    }
  }

  console.log('[OrganizerDB] Loaded', sessions.length, 'scheduled sessions');
  return sessions;
}
